import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Badge } from '@/shared/ui/badge';
import { FileText, Download, Mail, Calendar, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export const ExportReports: React.FC = () => {
  const { t } = useTranslation();

  const REPORT_TYPES = [
    {
      id: 'monthly',
      name: t('analytics.export.monthly', 'Monthly Summary'),
      description: t('analytics.export.monthlyDesc', 'Comprehensive overview of your monthly activities and performance'),
      icon: Calendar,
    },
    {
      id: 'score',
      name: t('analytics.export.scoreReport', 'Score Report (MFI)'),
      description: t('analytics.export.scoreReportDesc', 'Detailed credit score report for financial institutions'),
      icon: FileText,
    },
    {
      id: 'activity',
      name: t('analytics.export.activityLog', 'Activity Log'),
      description: t('analytics.export.activityLogDesc', 'Complete log of all farming activities with timestamps'),
      icon: CheckCircle,
    },
    {
      id: 'network',
      name: t('analytics.export.networkContrib', 'Network Contributions'),
      description: t('analytics.export.networkContribDesc', 'Summary of your network engagement and contributions'),
      icon: Mail,
    },
  ];

  const [selectedType, setSelectedType] = useState('monthly');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [email, setEmail] = useState('');
  const [generating, setGenerating] = useState(false);

  const handleGenerate = async (action: 'download' | 'email') => {
    if (!startDate || !endDate) {
      toast.error(t('analytics.export.selectDateRange', 'Please select date range'));
      return;
    }

    if (action === 'email' && !email) {
      toast.error(t('analytics.export.enterEmail', 'Please enter email address'));
      return;
    }

    setGenerating(true);

    try {
      // Simulate report generation
      await new Promise((resolve) => setTimeout(resolve, 2000));

      if (action === 'download') {
        toast.success(t('analytics.export.downloadSuccess', 'Report downloaded successfully!'));
      } else {
        toast.success(`Report sent to ${email}`);
      }
    } catch (error) {
      toast.error(t('analytics.export.generateFailed', 'Failed to generate report'));
    } finally {
      setGenerating(false);
    }
  };

  const selectedReport = REPORT_TYPES.find((r) => r.id === selectedType);

  return (
    <div className="space-y-6">
      {/* Report Type Selection */}
      <Card>
        <CardHeader>
          <CardTitle>{t('analytics.export.selectType', 'Select Report Type')}</CardTitle>
          <CardDescription>{t('analytics.export.selectTypeDesc', 'Choose the type of report you want to generate')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {REPORT_TYPES.map((report) => {
              const Icon = report.icon;
              return (
                <Card
                  key={report.id}
                  className={`cursor-pointer transition-all ${
                    selectedType === report.id
                      ? 'ring-2 ring-primary bg-primary/5'
                      : 'hover:bg-accent/5'
                  }`}
                  onClick={() => setSelectedType(report.id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <Icon className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-sm text-foreground mb-1">
                          {report.name}
                        </div>
                        <p className="text-xs text-muted-foreground">{report.description}</p>
                      </div>
                      {selectedType === report.id && (
                        <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>{t('analytics.export.config', 'Report Configuration')}</CardTitle>
          <CardDescription>{t('analytics.export.configDesc', 'Customize your report settings')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Date Range */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startDate">{t('analytics.export.startDate', 'Start Date')}</Label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endDate">{t('analytics.export.endDate', 'End Date')}</Label>
              <Input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          {/* Quick Date Ranges */}
          <div>
            <Label className="mb-2">{t('analytics.export.quickSelect', 'Quick Select')}</Label>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  const end = new Date();
                  const start = new Date();
                  start.setDate(start.getDate() - 7);
                  setStartDate(start.toISOString().split('T')[0]);
                  setEndDate(end.toISOString().split('T')[0]);
                }}
              >
                {t('analytics.export.last7Days', 'Last 7 Days')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  const end = new Date();
                  const start = new Date();
                  start.setDate(start.getDate() - 30);
                  setStartDate(start.toISOString().split('T')[0]);
                  setEndDate(end.toISOString().split('T')[0]);
                }}
              >
                {t('analytics.export.last30Days', 'Last 30 Days')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  const end = new Date();
                  const start = new Date();
                  start.setMonth(start.getMonth() - 3);
                  setStartDate(start.toISOString().split('T')[0]);
                  setEndDate(end.toISOString().split('T')[0]);
                }}
              >
                {t('analytics.export.last3Months', 'Last 3 Months')}
              </Button>
            </div>
          </div>

          {/* Email Address */}
          <div className="space-y-2">
            <Label htmlFor="email">{t('analytics.export.emailLabel', 'Email Address (Optional)')}</Label>
            <Input
              id="email"
              type="email"
              placeholder={t('analytics.export.emailPlaceholder', 'your@email.com')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              {t('analytics.export.emailNote', 'Leave empty to download directly, or enter email to receive report')}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Report Preview */}
      {selectedReport && (
        <Card>
          <CardHeader>
            <CardTitle>{t('analytics.export.reportPreview', 'Report Preview')}</CardTitle>
            <CardDescription>{t('analytics.export.reportPreviewDesc', 'What will be included in your report')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="text-sm text-foreground">{t('analytics.export.coverPage', 'Cover page with your details')}</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="text-sm text-foreground">{t('analytics.export.execSummary', 'Executive summary')}</span>
              </div>
              {selectedType === 'monthly' && (
                <>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-foreground">{t('analytics.export.activityStats', 'Activity statistics and trends')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-foreground">{t('analytics.export.scoreCharts', 'Score progression charts')}</span>
                  </div>
                </>
              )}
              {selectedType === 'score' && (
                <>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-foreground">{t('analytics.export.scoreBreakdown', 'Detailed score breakdown')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-foreground">{t('analytics.export.riskAssessment', 'Risk assessment')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-foreground">{t('analytics.export.verificationStatus', 'Verification status')}</span>
                  </div>
                </>
              )}
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="text-sm text-foreground">{t('analytics.export.appendix', 'Appendix with raw data')}</span>
              </div>
            </div>

            <div className="mt-4 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <p className="text-xs text-blue-600">
                <strong>{t('analytics.export.noteLabel', 'Note:')}</strong> {t('analytics.export.noteText', 'All reports are generated in PDF format with secure watermarks and QR codes for verification.')}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <Card>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Button
              size="lg"
              onClick={() => handleGenerate('download')}
              disabled={generating}
              className="gap-2"
            >
              <Download className="h-5 w-5" />
              {generating ? t('analytics.export.generating', 'Generating...') : t('analytics.export.download', 'Download Report')}
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => handleGenerate('email')}
              disabled={generating}
              className="gap-2"
            >
              <Mail className="h-5 w-5" />
              {generating ? t('analytics.export.sending', 'Sending...') : t('analytics.export.emailReport', 'Email Report')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
